import gsap from 'gsap';

import { nodeListToArray } from './dom';

/** Shared timing for step/tile highlight swaps (icon glow stays in sync with row lift). */
export const SBR_STEP_HIGHLIGHT_DURATION = 0.28;

export type TealIconVisualState = {
  backgroundColor: string;
  color: string;
  borderColor: string;
  boxShadow: string;
};

/** Teal ring icons on dark navy / hero glass cards. */
export const SBR_TEAL_ICON_ON_DARK: { idle: TealIconVisualState; active: TealIconVisualState } = {
  idle: {
    backgroundColor: 'rgba(0, 194, 171, 0.1)',
    color: '#00c2ab',
    borderColor: 'rgba(0, 194, 171, 0.35)',
    boxShadow: '0 0 16px rgba(0, 194, 171, 0.2)',
  },
  active: {
    backgroundColor: 'rgba(0, 194, 171, 0.28)',
    color: '#5effe8',
    borderColor: 'rgba(0, 194, 171, 0.65)',
    boxShadow: '0 0 0 2px rgba(0, 194, 171, 0.45), 0 0 24px rgba(0, 194, 171, 0.35)',
  },
};

/** Teal/blue icons on light advantage cards (homepage SBR2TH Advantage). */
export const SBR_TEAL_ICON_ON_LIGHT: { idle: TealIconVisualState; active: TealIconVisualState } = {
  idle: {
    backgroundColor: 'rgba(43, 147, 240, 0.1)',
    color: '#2b93f0',
    borderColor: 'transparent',
    boxShadow: '0 0 0 0 transparent',
  },
  active: {
    backgroundColor: 'rgba(0, 194, 171, 0.18)',
    color: '#00c2ab',
    borderColor: 'transparent',
    boxShadow: '0 0 0 2px rgba(0, 194, 171, 0.35), 0 0 20px rgba(0, 194, 171, 0.28)',
  },
};

/** Stop in-flight tweens before swapping the active step (avoids ring/card lag). */
export function killStepHighlightTweens(...lists: NodeListOf<HTMLElement>[]): void {
  if (!lists.length) return;
  gsap.killTweensOf(nodeListToArray(...lists));
}

/** Apply active/idle teal icon ring in one shot (inactive resets instantly). */
export function animateTealIconHighlight(
  icon: HTMLElement,
  active: boolean,
  palette: { idle: TealIconVisualState; active: TealIconVisualState } = SBR_TEAL_ICON_ON_DARK,
  activeScale = 1.1,
): void {
  const visual = active ? palette.active : palette.idle;

  if (active) {
    gsap.to(icon, {
      scale: activeScale,
      ...visual,
      duration: SBR_STEP_HIGHLIGHT_DURATION,
      ease: 'power2.out',
      overwrite: 'auto',
    });
  } else {
    gsap.set(icon, { scale: 1, ...visual });
  }
}

/** Infinite step cycle used by process tracks, advantage tiles, and hero graphs. */
export function createStepHighlightCycle(
  count: number,
  onStep: (index: number) => void,
  options?: { holdDuration?: number; repeatDelay?: number },
): gsap.core.Timeline | null {
  if (count <= 0) return null;

  onStep(0);

  const hold = options?.holdDuration ?? 0.95;
  const repeatDelay = options?.repeatDelay ?? 0.35;
  const tl = gsap.timeline({ repeat: -1, repeatDelay });

  for (let i = 0; i < count; i++) {
    tl.add(() => onStep(i));
    tl.to({}, { duration: hold });
  }

  return tl;
}
